import React from 'react';
import { DiffChunk as DiffChunkType } from '../../types';

interface DiffChunkProps {
  chunk: DiffChunkType;
  onAccept: () => void;
  onReject: () => void;
  applying?: boolean;
}

export function DiffChunk({ chunk, onAccept, onReject, applying = false }: DiffChunkProps) {
  const statusStyles = {
    pending: 'border-gray-300 bg-white',
    accepted: 'border-green-400 bg-green-50',
    rejected: 'border-red-400 bg-red-50 opacity-60'
  };

  return (
    <div className={`border rounded-lg mb-4 overflow-hidden ${statusStyles[chunk.status]}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">
            Lines {chunk.startLine}-{chunk.endLine}
          </span>
          {chunk.status !== 'pending' && (
            <span className={`text-xs px-2 py-0.5 rounded ${
              chunk.status === 'accepted'
                ? 'bg-green-100 text-green-700'
                : 'bg-red-100 text-red-700'
            }`}>
              {chunk.status === 'accepted' ? 'Accepted' : 'Rejected'}
            </span>
          )}
        </div>
        {chunk.status === 'pending' && (
          <div className="flex gap-2">
            <button
              onClick={onAccept}
              disabled={applying}
              className="px-3 py-1 text-sm bg-green-500 text-white rounded hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {applying ? 'Checking…' : 'Accept'}
            </button>
            <button
              onClick={onReject}
              disabled={applying}
              className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
            >
              Reject
            </button>
          </div>
        )}
      </div>

      {/* Explanation */}
      {chunk.explanation && (
        <div className="px-4 py-2 bg-blue-50 border-b text-sm text-blue-700">
          {chunk.explanation}
        </div>
      )}

      {/* Side by side diff */}
      <div className="grid grid-cols-2 divide-x">
        {/* Original */}
        <div className="p-4">
          <div className="text-xs font-medium text-gray-500 mb-2">Original</div>
          <pre className="text-sm font-mono whitespace-pre-wrap text-red-700 bg-red-50 p-2 rounded">
            {chunk.original}
          </pre>
        </div>

        {/* Suggested */}
        <div className="p-4">
          <div className="text-xs font-medium text-gray-500 mb-2">Suggested</div>
          <pre className="text-sm font-mono whitespace-pre-wrap text-green-700 bg-green-50 p-2 rounded">
            {chunk.suggested}
          </pre>
        </div>
      </div>
    </div>
  );
}
